import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Grid } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import {
  GridColDef,
  GridRowModel,
  GridToolbarContainer
} from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import OrderAPI from 'api/faber/OrderAPI'
import CustomSearchbar from 'common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import TabButton from 'common/TabButton/TabButton'
import TabPanel from 'common/TabPanel/TabPanel'
import { ColorBoxContent } from 'common/ui/colorBox'
import { getDate } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { OrderChangeState, OrderItem } from 'types/api/faber/order.type'

import DialogInputConfirmation from '../components/DialogInputConfirmation'

// const ORDER_LIMIT = 50 Remove order_limit temporarily

const AccountToolbar = () => {
  return (
    <GridToolbarContainer
      sx={{
        justifyContent: 'space-between'
      }}
    >
      <CustomSearchbar />
    </GridToolbarContainer>
  )
}

const getConfirmationMessage = (newRow: OrderItem) => {
  if (newRow.paymentStatus === OrderChangeState.SUCCESS) {
    return `This action confirms that ${newRow.userName} has successfully paid for order #${newRow.ID}.`
  } else {
    return `This action confirms that ${newRow.userName} has failed to pay for order #${newRow.ID}.`
  }
}

export default function ESimOrderManagementPage() {
  const [promiseArguments, setPromiseArguments] =
    useState<IPromiseArguments | null>()

  const [retrievedOrders, setRetrievedOrders] = useState<OrderItem[]>([])
  const [paymentSuccessState, setPaymentSuccessState] = useState<OrderItem[]>(
    []
  )
  const [paymentFailedState, setPaymentFailedState] = useState<OrderItem[]>([])
  const [paymentOthersState, setPaymentOthersState] = useState<OrderItem[]>([])

  const [dataArray, setDataArray] = useState<IDataProps[]>([])
  const [tabValue, setTabValue] = useState(0)
  const handleTabChange = (_, newNum) => setTabValue(newNum)
  const faberToken = OrbitSession.get('faberToken')

  const { data: getAllOrders } = useQuery(
    ['getAllOrders', 'esims'],
    async () => {
      return OrderAPI.getAllOrders(faberToken)
    }
  )

  const { mutate: updateOrderPaymentStatus } = useMutation(
    OrderAPI.updateOrderPaymentStatus,
    {
      onSuccess: () => {
        if (promiseArguments) {
          toast.success('Order payment status updated')

          const { newRow, resolve } = promiseArguments

          resolve(newRow)

          setRetrievedOrders((old) => {
            return old.map((order) => {
              if (order.ID === newRow.ID) {
                order.paymentStatus = newRow.paymentStatus
              }
              return order
            })
          })

          setPromiseArguments(null)
        }
      },
      onError: () => {
        if (promiseArguments) {
          toast.error('Failed to update order payment status')

          const { oldRow, resolve } = promiseArguments
          resolve(oldRow)

          setPromiseArguments(null)
        }
      }
    }
  )

  const handleUpdateConfirmation = async (paymentID: string) => {
    if (promiseArguments) {
      const { newRow } = promiseArguments
      updateOrderPaymentStatus({
        ID: newRow.ID,
        paymentStatus: newRow.paymentStatus,
        paymentID
      })
    }
  }

  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) =>
      new Promise<GridRowModel>((resolve, reject) => {
        const mutation = getConfirmationMessage(newRow as OrderItem)

        if (mutation) {
          setPromiseArguments({ resolve, reject, newRow, oldRow })
        } else {
          resolve(oldRow)
        }
      }),
    []
  )

  useEffect(() => {
    /* TO BE EDITED ONCE THE ORDER STATES ARE FINALISED? */

    // Filter for payment success orders
    const paymentSuccess = retrievedOrders ? retrievedOrders.filter(
      (o) => o.paymentStatus === OrderChangeState.SUCCESS
    ) : []

    // Filter for payment failed orders
    const paymentFailedState = retrievedOrders ? retrievedOrders.filter(
      (o) => o.paymentStatus === OrderChangeState.FAILED
    ) : []

    // Filter for all other orders states
    const others = retrievedOrders ? retrievedOrders.filter(
      (o) =>
        ![OrderChangeState.SUCCESS, OrderChangeState.FAILED].includes(
          o.paymentStatus
        )
    ) : []

    setPaymentSuccessState(paymentSuccess)
    setPaymentFailedState(paymentFailedState)
    setPaymentOthersState(others)

    setDataArray([
      { title: 'All', info: retrievedOrders?.length || 0 },
      { title: 'Payment Success', info: paymentSuccess.length },
      { title: 'Payment Failed', info: paymentFailedState.length },
      { title: 'Others', info: others.length }
    ])
  }, [retrievedOrders || []])

  useEffect(() => {
    if (getAllOrders) {
      const { data: allOrders } = getAllOrders.data
      setRetrievedOrders(allOrders || [])
    }
  }, [getAllOrders])

  const columns: GridColDef[] = [
    { field: 'ID', headerName: 'Order ID.', width: 100 },
    { field: 'userName', headerName: 'Name', width: 100 },
    { field: 'userEmail', headerName: 'Email', width: 100 },
    { field: 'ICCID', headerName: 'Iccid', width: 100 },
    {
      field: 'paymentStatus',
      headerName: 'Status',
      width: 50,
      type: 'singleSelect',
      editable: true,
      valueOptions: () => [OrderChangeState.SUCCESS, OrderChangeState.FAILED]
    },
    {
      field: 'CreatedAt',
      headerName: 'Created At',
      width: 50,
      valueGetter: (params) => {
        const order: OrderItem = params.row
        return getDate(order.CreatedAt)
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 50
      // renderCell: (params) => {
      //   // const order: OrderItem = params.row
      //   return (
      //     <IconButton
      //       onClick={() => navigate(`/hq/manage-orders/`)}
      //       data-cy='actionsBtn'
      //     >
      //       <Settings />
      //     </IconButton>
      //   )
      // }
    }
  ]

  const variantPicker = (allocatedNum) =>
    allocatedNum === tabValue ? 'filled' : 'tertiary'
  const colorPicker = (allocatedNum) =>
    allocatedNum === tabValue ? 'white' : 'black'

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <ColorBoxContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            {dataArray.map((label, index) => {
              return (
                <TabButton
                  key={label.title}
                  label={`${label.title} (${label.info})`}
                  color={colorPicker(index)}
                  variant={variantPicker(index)}
                  value={index}
                  onClick={(num) => setTabValue(num)}
                />
              )
            })}
          </Tabs>
        </ColorBoxContent>
      </Grid>

      <Grid item>
        <DialogInputConfirmation
          {...{
            promiseArguments,
            setPromiseArguments,
            dialogTitle: 'Are you sure?',
            getConfirmationMessage,
            dialogConfirmation: handleUpdateConfirmation
          }}
        />
        {[
          retrievedOrders,
          paymentSuccessState,
          paymentFailedState,
          paymentOthersState
        ].map((orders, index) => {
          return (
            <TabPanel
              key={`OrderManagementPage-tab${index}`}
              value={tabValue}
              index={index}
            >
              <ColorBoxContent>
                <MUIDataGrid
                  getRowId={(order: OrderItem) => order.ID}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'createdAt', sort: 'desc' }]
                    }
                  }}
                  rows={orders}
                  columns={columns}
                  experimentalFeatures={{ newEditingApi: true }}
                  processRowUpdate={processRowUpdate}
                  components={{ Toolbar: AccountToolbar }}
                />
              </ColorBoxContent>
            </TabPanel>
          )
        })}
      </Grid>
    </Grid>
  )
}

interface IDataProps {
  title: string
  info: number
}

interface IPromiseArguments {
  newRow: GridRowModel
  oldRow: GridRowModel
  resolve: (row: GridRowModel) => void
  reject: (row: GridRowModel) => void
}
